import { Box, HStack, Icon, Popover, Text } from "@chakra-ui/react"
import { FaCircleCheck, FaCircleQuestion } from "react-icons/fa6"
import PaginatedFigureCard from "@hydrologiq/shared/figures/generic/PaginatedFigureCard"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"
import {
  computeVehiclesToFill,
  fillExplanation,
  fillPercentParts,
} from "@hydrologiq/shared/figures/headlines/vehicleTypes"
import VehicleIcon from "@hydrologiq/shared/figures/headlines/vehicleIcons"

/**
 * Green where there is nothing to act on, blue where there is only something to know — the palette
 * the electrolyser cards already use. A question mark rather than an "i" because the card's header
 * already carries an info icon, and amber is reserved here for states that invite attention.
 *
 * `FaCircleQuestion` is also electrolyserStatusVisuals' grey "unknown" glyph, which can appear on
 * the same strip; colour is the only thing separating the two.
 */
const FULL_VISUAL = { icon: FaCircleCheck, color: "#50c878" }
const CLIPPED_VISUAL = { icon: FaCircleQuestion, color: "#39aed2" }

/**
 * Matches ElectrolyserStackFigure's flow reading. Applied on open as well as hover so it stays clear
 * which reading the panel belongs to.
 */
const TRIGGER_HIGHLIGHT = { bg: "#EEF2F4", "& span": { textDecorationColor: "#39aed2" } }

interface VehiclesToFillFigureProps {
  /** Current stored hydrogen weight (kg) — the installation `hydrogenLevel` value. */
  currentWeightKg?: number | null
  loading?: boolean
  compact?: boolean
}

/**
 * Burnaston overview card — "Capacity to Fill".
 *
 * The heading is display copy only; the figure key (`VEHICLES_TO_FILL`) and the `vehicles_to_fill`
 * figure type stay as they are — a system's `figures` config and core both key off that string.
 *
 * Computed on the frontend (see vehicleTypes.ts): per vehicle type, how many could be filled from
 * the current stored hydrogen, and how full each fill gets that vehicle. Pages between vehicle types
 * via PaginatedFigureCard. All values are kg, so no NL→kg conversion here.
 */
function VehiclesToFillFigure({
  currentWeightKg,
  loading,
  compact = isCompactFigure("summary"),
}: VehiclesToFillFigureProps) {
  const vehicles = computeVehiclesToFill(currentWeightKg)

  return (
    <PaginatedFigureCard
      heading="Fill Capacity"
      loading={loading}
      compact={compact}
      items={vehicles}
      emptyState={
        <FigureContent.Root>
          <FigureContent.SubText>Number of cars</FigureContent.SubText>
          <FigureContent.Value>—</FigureContent.Value>
        </FigureContent.Root>
      }
      renderItem={(entry) => {
        const visual = entry.fillFraction >= 1 ? FULL_VISUAL : CLIPPED_VISUAL
        const parts = fillPercentParts(entry.fillFraction)
        return (
          <FigureContent.Root>
            <HStack gap={2} alignItems="center">
              <FigureContent.SubText>{entry.vehicle}</FigureContent.SubText>
              <VehicleIcon vehicle={entry.vehicle} size="1.25em" />
            </HStack>
            {/* Top-aligned, not centred: the note wraps to two lines, and centring would push the
                count down by half the extra height, out of line with every other card's value. */}
            <HStack
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              minWidth={0}
            >
              <Box flexShrink={0}>
                <FigureContent.Value>{entry.count}</FigureContent.Value>
              </Box>
              {/* Right-anchored rather than inset from the count, so the note holds its place
                  whether the count reads 1, 14 or 100. `minWidth={0}` permits the wrap; without it
                  the flex item keeps its max-content width and spills past the card. */}
              <Box minWidth={0}>
                {/* Click to open, click away or Escape to close — the same gesture as the card
                    header's info icon, and unlike a hover panel it can be read at leisure.
                    `portalled={false}` follows RunDry and the shared Popover; the z-index keeps the
                    panel above the neighbouring cards rather than clipped between them. */}
                <Popover.Root
                  positioning={{ placement: "top" }}
                  portalled={false}
                  closeOnInteractOutside
                  closeOnEscape
                >
                  <Popover.Trigger
                    cursor="pointer"
                    px="5px"
                    mx="-5px"
                    py="2px"
                    mt="0px"
                    borderRadius="4px"
                    transition="background 140ms"
                    _hover={TRIGGER_HIGHLIGHT}
                    _open={TRIGGER_HIGHLIGHT}
                    _focusVisible={{ outline: "2px solid #39aed2", outlineOffset: "1px" }}
                  >
                    <HStack gap={1.5} alignItems="center" minWidth={0}>
                      {/* Dotted underline as the affordance: solid already means a drilldown, dashed
                          means click-to-edit and underline-on-hover means a link, so dotted is the
                          one treatment still free. `as="span"` because the trigger is a <button>,
                          which may only contain phrasing content — it also lets the trigger recolour
                          the whole label with a single `& span` rule. */}
                      <Text
                        as="span"
                        display="block"
                        fontSize="13px"
                        fontWeight="500"
                        color="primary_light"
                        lineHeight="1.35"
                        textAlign="start"
                        whiteSpace="nowrap"
                        textDecoration="underline"
                        textDecorationStyle="dotted"
                        textDecorationColor="#C4C4C4"
                        textUnderlineOffset="3px"
                      >
                        {parts.lead ? `${parts.lead} ` : ""}
                        {/* Fixed width for the longest reading, so neither the text nor the icon
                            shifts between "50%" and "100%". The underline is repeated because
                            decorations don't propagate into atomic inline boxes, and an inline-block
                            is one — without it the percentage is the only undecorated word here. */}
                        <Text
                          as="span"
                          display="inline-block"
                          minWidth="2.4em"
                          fontVariantNumeric="tabular-nums"
                          textDecoration="underline"
                          textDecorationStyle="dotted"
                          textDecorationColor="#C4C4C4"
                          textUnderlineOffset="3px"
                        >
                          {parts.percent}
                        </Text>{" "}
                        {parts.trail}
                      </Text>
                      {/* Same expression as the electrolyser status icon — em resolves against the
                          inherited base, not the 13px label — so the two cards' glyphs match. Last
                          in a right-anchored note, so it lands on the content edge where the
                          header's pager ends. */}
                      <Icon
                        as={visual.icon}
                        color={visual.color}
                        boxSize="1.15em"
                        flexShrink={0}
                        ms={1}
                        aria-hidden
                      />
                    </HStack>
                  </Popover.Trigger>
                  <Popover.Positioner zIndex={99}>
                    <Popover.Content width="auto" maxWidth="300px" color="primary">
                      <Popover.Arrow />
                      <Box padding={3}>
                        <Text fontSize="13px" color="primary" lineHeight="1.5">
                          {fillExplanation(entry)}
                        </Text>
                      </Box>
                    </Popover.Content>
                  </Popover.Positioner>
                </Popover.Root>
              </Box>
            </HStack>
          </FigureContent.Root>
        )
      }}
    />
  )
}

export default VehiclesToFillFigure
